import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { apiClient } from "../api/client";
import { HolidayClubAttendanceNumberResponse } from "../api/PartnerPortalGatewayApi";

interface UseHolidayClubAttendanceResult {
  data: HolidayClubAttendanceNumberResponse[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

// Hook for getting holiday club attendance by year and term
export const useHolidayClubAttendance = (
  serviceId: number | null,
  year?: number,
  term?: number,
): UseHolidayClubAttendanceResult => {
  const currentYear = new Date().getFullYear();
  const finalYear = year || currentYear;
  const finalTerm = term || 1;

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["holidayClubAttendance", serviceId, finalYear, finalTerm],
    queryFn: async (): Promise<HolidayClubAttendanceNumberResponse[]> => {
      if (!serviceId) {
        return [];
      }

      const response = await apiClient.attendance.getHolidayClubAttendanceNumberByService(
        serviceId,
        finalYear,
        finalTerm,
      );

      console.log("Holiday Club Attendance API Response:", response);

      return response || [];
    },
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    data: queryData || [],
    loading: isLoading,
    error: error?.message || null,
    refetch,
  };
};

// Hook for getting holiday club attendance by date range
export const useHolidayClubAttendanceByDate = (
  serviceId: number | null,
  startDate?: dayjs.Dayjs,
  endDate?: dayjs.Dayjs,
): UseHolidayClubAttendanceResult => {
  // Default to current month if no dates provided
  const defaultStartDate = dayjs().startOf("month");
  const defaultEndDate = dayjs().endOf("month");

  const finalStartDate = startDate || defaultStartDate;
  const finalEndDate = endDate || defaultEndDate;

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [
      "holidayClubAttendanceByDate",
      serviceId,
      finalStartDate.format("YYYY-MM-DD"),
      finalEndDate.format("YYYY-MM-DD"),
    ],
    queryFn: async (): Promise<HolidayClubAttendanceNumberResponse[]> => {
      if (!serviceId) {
        return [];
      }

      const response = await apiClient.attendance.getHolidayClubAttendanceNumberByServiceAndDate(
        serviceId,
        finalStartDate,
        finalEndDate,
      );

      console.log("Holiday Club Attendance By Date API Response:", response);

      return response || [];
    },
    enabled: !!serviceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    data: queryData || [],
    loading: isLoading,
    error: error?.message || null,
    refetch,
  };
};
